import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

/** 列の型。select は保持する値が文字列か数値 ID かで分ける（値型を列定義から一意に決めるため）。 */
export type FilterColumnType = 'text' | 'number' | 'time' | 'enumSelect' | 'idSelect' | 'boolean';

export type TextOperator = 'contains' | 'notContains' | 'equals' | 'notEquals' | 'empty' | 'notEmpty';
export type NumberOperator = 'gte' | 'lte' | 'eq' | 'neq' | 'between';
export type TimeOperator = 'timeGte' | 'timeLte' | 'timeEq';
export type SelectOperator = 'anyOf' | 'noneOf';
export type BooleanOperator = 'is';

export type FilterOperator = TextOperator | NumberOperator | TimeOperator | SelectOperator | BooleanOperator;

/**
 * 値は kind 付きの discriminated union。緩い union だと
 * (a) boolean の「未選択」と false (b) 数値 ID (c) 値入力を持たない演算子
 * を型で区別できない。
 */
export type FilterValue =
  | { kind: 'none' }
  | { kind: 'text'; value: string }
  | { kind: 'number'; value: number | null }
  | { kind: 'numberRange'; min: number | null; max: number | null }
  | { kind: 'time'; value: string }
  | { kind: 'enum'; values: string[] }
  | { kind: 'id'; values: number[] }
  | { kind: 'boolean'; value: boolean | null };

export type FilterRow = {
  id: string;
  /** 先頭行は 'where' 固定・削除不可 */
  combinator: 'where' | 'and' | 'or';
  column: string;
  operator: FilterOperator;
  value: FilterValue;
};

type LegRun = ShipmentOrderLegRunForPlanEntity;
type BaseColumn = { key: string; label: string };

/** 列定義。getValue は元データから比較対象を取り出す。 */
export type FilterColumnDef =
  | (BaseColumn & { type: 'text'; getValue: (lr: LegRun) => string | string[] | null | undefined })
  | (BaseColumn & { type: 'number'; getValue: (lr: LegRun) => number | null | undefined })
  | (BaseColumn & { type: 'time'; getValue: (lr: LegRun) => string | null | undefined })
  | (BaseColumn & { type: 'enumSelect'; options: { value: string; label: string }[]; getValue: (lr: LegRun) => string[] | null | undefined })
  | (BaseColumn & { type: 'idSelect'; options: { value: number; label: string }[]; getValue: (lr: LegRun) => number[] | null | undefined })
  | (BaseColumn & { type: 'boolean'; getValue: (lr: LegRun) => boolean | null | undefined });

export const OPERATORS_BY_TYPE: Record<FilterColumnType, { value: FilterOperator; label: string }[]> = {
  text: [
    { value: 'contains', label: '...を含む' },
    { value: 'notContains', label: '...を含まない' },
    { value: 'equals', label: '...と一致する' },
    { value: 'notEquals', label: '...と一致しない' },
    { value: 'empty', label: '空である' },
    { value: 'notEmpty', label: '空でない' },
  ],
  number: [
    { value: 'gte', label: '≥ 以上' },
    { value: 'lte', label: '≤ 以下' },
    { value: 'eq', label: '= 等しい' },
    { value: 'neq', label: '≠ 等しくない' },
    { value: 'between', label: '範囲内' },
  ],
  time: [
    { value: 'timeGte', label: '指定時刻以降' },
    { value: 'timeLte', label: '指定時刻以前' },
    { value: 'timeEq', label: '指定時刻と一致' },
  ],
  enumSelect: [
    { value: 'anyOf', label: 'いずれかに一致' },
    { value: 'noneOf', label: 'いずれにも一致しない' },
  ],
  idSelect: [
    { value: 'anyOf', label: 'いずれかに一致' },
    { value: 'noneOf', label: 'いずれにも一致しない' },
  ],
  // 可 / 不可 は値で選ぶ。演算子と値の両方を可否にすると矛盾行が作れるため 1 種類に固定する。
  boolean: [{ value: 'is', label: '一致する' }],
};

/**
 * 期待する値の kind は「列の型 × 演算子」で決まる。
 * 列だけで決め打ちすると text の empty 行や number の between 行が型不一致と誤判定される。
 * UI の値入力・isActiveFilterRow・evaluate・プリセット検証はすべてこの関数を使うこと。
 */
export function expectedValueKind(type: FilterColumnType, operator: FilterOperator): FilterValue['kind'] {
  switch (type) {
    case 'text':
      return operator === 'empty' || operator === 'notEmpty' ? 'none' : 'text';
    case 'number':
      return operator === 'between' ? 'numberRange' : 'number';
    case 'time':
      return 'time';
    case 'enumSelect':
      return 'enum';
    case 'idSelect':
      return 'id';
    case 'boolean':
      return 'boolean';
    default:
      return 'text';
  }
}

/**
 * Map（/map）のシナリオ保存・再現用フィルタスナップショット。
 * 旧画面 (`serverPresets.ts` の encodePresetRows) のような V3 `{logicalOperator,key,operator,value}`
 * 形式への変換はしない。数値範囲・複数選択・時刻・boolean 等を表現できず保存のたびに条件が
 * 欠落するため、version 付きの別契約としてそのまま持つ。
 */
export type MapScenarioFilterV1 = {
  kind: 'map';
  version: 1;
  freeText: string;
  rows: FilterRow[];
};

/** 列型に応じた「未入力」の初期値。UI が列や演算子を切り替えたときに使う。 */
export function emptyValueFor(type: FilterColumnType, operator: FilterOperator): FilterValue {
  switch (expectedValueKind(type, operator)) {
    case 'none': return { kind: 'none' };
    case 'text': return { kind: 'text', value: '' };
    case 'number': return { kind: 'number', value: null };
    case 'numberRange': return { kind: 'numberRange', min: null, max: null };
    case 'time': return { kind: 'time', value: '' };
    case 'enum': return { kind: 'enum', values: [] };
    case 'id': return { kind: 'id', values: [] };
    case 'boolean': return { kind: 'boolean', value: null };
    default: return { kind: 'text', value: '' };
  }
}
